import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  Calendar,
  Clock3,
  ExternalLink,
  FileText,
  History,
  Link2,
  ListOrdered,
  Loader2,
  Paperclip,
  Receipt,
  User,
  Wallet,
  X,
} from "lucide-react";
import {
  getInvoiceDetail,
  getStatusLabel,
} from "../../../api/finance/invoices/invoiceRequests";
import { formatDateVN, formatDateTimeVN } from "../../../utils/formatUtils";

const money = (v) => `${Number(v || 0).toLocaleString("vi-VN")} ₫`;

const InvoiceDetailModal = ({ isOpen, onClose, invoiceId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !invoiceId) return;
    setLoading(true);
    getInvoiceDetail(invoiceId)
      .then((data) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [isOpen, invoiceId]);

  useEffect(() => {
    if (!isOpen) setDetail(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const item = detail;
  const isSale = item?.invoice_type === "sale";
  const vatPct = (() => {
    if (!item) return null;
    const sub = Number(item.subtotal || 0);
    const vat = Number(item.vat_amount || 0);
    if (sub <= 0) return null;
    return Math.round((vat / sub) * 10000) / 100;
  })();

  const hasLinkedDocs =
    item?.linked_payment_code ||
    item?.linked_receipt_code ||
    item?.linked_payment_id ||
    item?.linked_receipt_id;

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex flex-col bg-white sm:bg-slate-900/60 sm:backdrop-blur-md sm:items-center sm:justify-center sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="flex flex-col w-full h-full min-h-0 bg-white sm:max-w-5xl sm:max-h-[92vh] sm:h-auto sm:rounded-[1.75rem] sm:shadow-2xl sm:border sm:border-slate-100 overflow-hidden"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 28 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-7 py-3.5 sm:py-5 border-b border-slate-200 bg-gradient-to-r from-red-50 via-white to-white flex justify-between items-start gap-3 shrink-0 pt-[max(0.875rem,env(safe-area-inset-top))]">
              <div className="flex items-center gap-3 min-w-0">
                <span className="p-2 sm:p-2.5 bg-red-600 rounded-2xl text-white shadow-lg shadow-red-200 shrink-0">
                  <FileText size={18} className="sm:w-5 sm:h-5" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">
                    Chi tiết hóa đơn
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate">
                    {item?.invoice_number || (loading ? "Đang tải..." : "—")}
                    {item?.partner_name ? ` · ${item.partner_name}` : ""}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-red-600 p-2 rounded-xl hover:bg-red-50 shrink-0"
                aria-label="Đóng"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]">
              {loading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="animate-spin text-red-600" size={36} />
                </div>
              ) : !item ? (
                <p className="text-center text-slate-400 py-20 text-sm">
                  Không tải được chi tiết hóa đơn.
                </p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.9fr)] lg:divide-x divide-slate-100">
                  <div className="p-4 sm:p-7 space-y-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider border ${
                          isSale
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : "bg-red-50 text-red-700 border-red-100"
                        }`}
                      >
                        {isSale ? "Hóa đơn bán" : "Hóa đơn mua"}
                      </span>
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider border bg-slate-50 text-slate-700 border-slate-200">
                        {getStatusLabel(
                          item.invoice_type,
                          item.processing_status
                        )}
                      </span>
                    </div>

                    <div
                      className={`rounded-2xl p-4 sm:p-5 text-white shadow-lg ${
                        isSale
                          ? "bg-gradient-to-br from-emerald-600 to-emerald-800"
                          : "bg-gradient-to-br from-red-600 to-red-800"
                      }`}
                    >
                      <p className="text-[11px] font-bold uppercase tracking-widest text-white/60">
                        Tổng tiền
                      </p>
                      <p className="text-2xl sm:text-3xl font-black mt-1 tabular-nums break-all">
                        {money(item.total_amount)}
                      </p>
                      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/15 pt-4 text-sm">
                        <div>
                          <p className="text-white/55 text-xs">Trước VAT</p>
                          <p className="font-semibold tabular-nums mt-0.5">
                            {money(item.subtotal)}
                          </p>
                        </div>
                        <div>
                          <p className="text-white/55 text-xs">
                            VAT{vatPct != null ? ` (${vatPct}%)` : ""}
                          </p>
                          <p className="font-semibold tabular-nums mt-0.5">
                            {money(item.vat_amount)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {Array.isArray(item.items) && item.items.length > 0 ? (
                      <Section
                        title={`Mặt hàng (${item.items.length})`}
                        icon={ListOrdered}
                      >
                        <InvoiceItemsList items={item.items} />
                      </Section>
                    ) : null}

                    <Section title="Thông tin chứng từ" icon={FileText}>
                      <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
                        <InfoRow
                          label="Số hóa đơn"
                          value={item.invoice_number}
                        />
                        <InfoRow
                          label="Ngày hóa đơn"
                          value={
                            item.invoice_date
                              ? formatDateVN(item.invoice_date)
                              : "—"
                          }
                        />
                        <InfoRow
                          label="Hạn thanh toán"
                          value={
                            item.due_date ? formatDateVN(item.due_date) : "—"
                          }
                        />
                        <InfoRow
                          label="Chi nhánh"
                          value={item.branch_name || "—"}
                          icon={Building2}
                        />
                        <InfoRow
                          label={isSale ? "Khách hàng" : "Nhà cung cấp"}
                          value={item.partner_name || "—"}
                        />
                        <InfoRow
                          label="Mã số thuế"
                          value={item.partner_tax_code || "—"}
                        />
                        <InfoRow
                          label="Người ghi nhận"
                          value={item.created_by_name || "—"}
                          icon={User}
                        />
                        <InfoRow
                          label="Ngày ghi nhận"
                          value={
                            item.created_at
                              ? formatDateTimeVN(item.created_at)
                              : "—"
                          }
                          icon={Clock3}
                        />
                      </div>
                    </Section>

                    {(item.description || item.note) && (
                      <Section title="Diễn giải" icon={FileText}>
                        {item.description ? (
                          <div className="mb-3 rounded-2xl border-2 border-slate-200 px-3.5 py-3">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                              Nội dung / hàng hóa dịch vụ
                            </p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        ) : null}
                        {item.note ? (
                          <div className="rounded-xl bg-amber-50 border border-amber-100 px-3.5 py-3">
                            <p className="text-[10px] font-bold text-amber-700/70 uppercase tracking-widest mb-1">
                              Ghi chú nội bộ
                            </p>
                            <p className="text-sm text-amber-900 whitespace-pre-wrap">
                              {item.note}
                            </p>
                          </div>
                        ) : null}
                      </Section>
                    )}

                    {item.thumbnail_url || item.attachment_url ? (
                      <Section title="Tài liệu đính kèm" icon={Paperclip}>
                        <div className="space-y-3">
                          {item.thumbnail_url ? (
                            <div className="overflow-hidden rounded-xl border-2 border-red-100 bg-white">
                              <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-red-700 bg-red-50/80 border-b border-red-100">
                                Xem nhanh (thumbnail DB — không gọi GCS)
                              </p>
                              <img
                                src={item.thumbnail_url}
                                alt={item.attachment_name || "Hóa đơn"}
                                className="mx-auto max-h-80 w-full object-contain bg-slate-50"
                              />
                            </div>
                          ) : null}
                          {item.attachment_url ? (
                            <a
                              href={item.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 rounded-xl border-2 border-red-200 bg-red-50/60 px-4 py-3 hover:bg-red-50 transition-colors"
                            >
                              <span className="h-10 w-10 rounded-xl bg-white border border-red-100 text-red-600 flex items-center justify-center shrink-0">
                                <FileText size={18} />
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-800 truncate">
                                  {item.attachment_name || "file-dinh-kem"}
                                </p>
                                <p className="text-xs text-red-600 font-medium mt-0.5">
                                  Mở bản gốc (GCS)
                                </p>
                              </div>
                              <ExternalLink
                                size={16}
                                className="text-slate-400 shrink-0"
                              />
                            </a>
                          ) : null}
                        </div>
                      </Section>
                    ) : null}
                  </div>

                  <div className="p-4 sm:p-7 bg-slate-50/80 space-y-5 border-t lg:border-t-0 border-slate-200">
                    <Section title="Chứng từ liên kết" icon={Link2}>
                      {hasLinkedDocs ? (
                        <div className="space-y-2.5">
                          {item.linked_payment_code ||
                          item.linked_payment_id ? (
                            <LinkedDocCard
                              icon={Wallet}
                              label="Đề nghị thanh toán"
                              code={item.linked_payment_code || "—"}
                              tone="red"
                            />
                          ) : null}
                          {item.linked_receipt_code ||
                          item.linked_receipt_id ? (
                            <LinkedDocCard
                              icon={Receipt}
                              label="Phiếu thu"
                              code={item.linked_receipt_code || "—"}
                              tone="emerald"
                            />
                          ) : null}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic py-2">
                          Chưa liên kết chứng từ thanh toán / phiếu thu.
                        </p>
                      )}
                    </Section>

                    <Section title="Nhật ký thao tác" icon={History}>
                      {Array.isArray(item.logs) && item.logs.length > 0 ? (
                        <div className="relative space-y-0">
                          <div className="absolute left-[15px] top-3 bottom-3 w-px bg-slate-200" />
                          {item.logs.map((log) => (
                            <div
                              key={log.id}
                              className="relative flex gap-3 py-3 first:pt-0 last:pb-0"
                            >
                              <span className="relative z-[1] mt-1 h-[30px] w-[30px] rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                                <Calendar
                                  size={13}
                                  className="text-slate-400"
                                />
                              </span>
                              <div className="min-w-0 flex-1 rounded-xl border-2 border-slate-200 bg-white px-3.5 py-3 shadow-sm">
                                <p className="text-sm font-semibold text-slate-800">
                                  {log.note || log.action || "Thao tác"}
                                </p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {log.actor_name || "—"} ·{" "}
                                  {log.created_at
                                    ? formatDateTimeVN(log.created_at)
                                    : "—"}
                                </p>
                                {log.from_status && log.to_status ? (
                                  <p className="text-[11px] font-bold text-red-600 mt-1.5">
                                    {getStatusLabel(
                                      item.invoice_type,
                                      log.from_status
                                    )}{" "}
                                    →{" "}
                                    {getStatusLabel(
                                      item.invoice_type,
                                      log.to_status
                                    )}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 italic py-2">
                          Chưa có nhật ký thao tác.
                        </p>
                      )}
                    </Section>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} className="text-slate-400" />
        <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-400">
          {title}
        </h4>
      </div>
      {children}
    </section>
  );
}

/** Bỏ hậu tố "STT n" trùng với số dòng; ẩn note chỉ là STT. */
function displayItemName(name) {
  return String(name || "")
    .replace(/\s*STT\s*\d+\s*$/i, "")
    .trim() || "—";
}

function displayItemNote(note) {
  const s = String(note || "").trim();
  if (!s || /^STT\s*\d+$/i.test(s)) return "";
  return s.replace(/(?:^|\s*·\s*)STT\s*\d+/gi, "").replace(/^\s*·\s*/, "").trim();
}

function InvoiceItemsList({ items = [] }) {
  const linesSubtotal = items.reduce(
    (acc, line) => acc + (Number(line.amount) || 0),
    0
  );

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {items.map((line, idx) => {
          const no = String(line.line_no || idx + 1).padStart(2, "0");
          const note = displayItemNote(line.note);
          const qty = Number(line.quantity || 0).toLocaleString("vi-VN");
          return (
            <li
              key={line.id || idx}
              className="px-3.5 sm:px-4 py-3.5 hover:bg-slate-50/70 transition-colors"
            >
              <div className="flex gap-3">
                <span className="mt-0.5 h-7 w-7 shrink-0 rounded-lg bg-red-50 text-red-700 text-[11px] font-black flex items-center justify-center tabular-nums">
                  {no}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-800 leading-snug">
                      {displayItemName(line.item_name)}
                    </p>
                    <p className="shrink-0 text-sm font-black tabular-nums text-red-700 text-right">
                      {money(line.amount)}
                    </p>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                      {line.unit || "—"}
                    </span>
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
                      SL {qty}
                    </span>
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-600">
                      ĐG {money(line.unit_price)}
                    </span>
                  </div>
                  {note ? (
                    <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">
                      {note}
                    </p>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between gap-3 border-t-2 border-slate-100 bg-slate-50/80 px-3.5 sm:px-4 py-2.5">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          Tổng dòng (trước VAT)
        </span>
        <span className="text-sm font-black tabular-nums text-slate-800">
          {money(linesSubtotal)}
        </span>
      </div>
    </div>
  );
}

/** Mobile/desktop: label trái — value phải */
function InfoRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3.5 py-3">
      <p className="text-[11px] sm:text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 shrink-0 max-w-[42%]">
        {Icon ? <Icon size={12} className="shrink-0" /> : null}
        <span>{label}</span>
      </p>
      <p className="text-sm font-semibold text-slate-800 text-right break-words min-w-0 flex-1">
        {value || "—"}
      </p>
    </div>
  );
}

function LinkedDocCard({ icon: Icon, label, code, tone = "red" }) {
  const toneCls =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50/70 text-emerald-700"
      : "border-red-200 bg-red-50/70 text-red-700";
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border-2 px-3.5 py-3 ${toneCls}`}
    >
      <span className="h-9 w-9 rounded-xl bg-white/80 flex items-center justify-center shrink-0">
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1 flex items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-70 shrink-0">
          {label}
        </p>
        <p className="text-sm font-bold truncate text-right">{code}</p>
      </div>
    </div>
  );
}

export default InvoiceDetailModal;
